import usePrivateAxios from '@/common/api/usePrivateAxios';
import { ErrorResponse } from '@/common/types/errorResponse';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { UpdateDashboardComponentValidationSchema } from '../../schemas/dashboards/dashboardComponent';
import { DashboardComponentDateFilters, DashboardComponentFilters, DashboardComponentGeneratorPropertiesFilter, DashboardComponentGeneratorPropertiesFilterMode } from '../../types/dashboards/dashboardComponent.type';

interface UseUpdateDashboardComponentsProps {
  dashboardId: string | undefined;
  dashboardComponentId: string | undefined;
  options?: Partial<Omit<UseMutationOptions<
    unknown,
    ErrorResponse,
    UpdateDashboardComponentValidationSchema
  >, 'mutationKey' | 'mutationFn'>>;
}

export const useUpdateDashboardComponents = ({
  dashboardId,
  dashboardComponentId,
  options,
}: UseUpdateDashboardComponentsProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['updateDashboardComponent', dashboardComponentId],
    mutationFn: (form) => {
      const parsedFilters = form.filters?.filter((filter) => filter.checked).reduce((prev, filter) => {
        if (!filter.value) {
          return prev;
        }

        if (filter.key === DashboardComponentDateFilters.StartDate || filter.key === DashboardComponentDateFilters.EndDate) {
          return [
            ...prev,
            {
              key: filter.key,
              value: filter.value,
              mode: DashboardComponentGeneratorPropertiesFilterMode.Hidden,
            },
          ];
        }

        return [
          ...prev,
          {
            key: filter.key as DashboardComponentFilters,
            value: filter.value?.value ?? filter.value,
            mode: DashboardComponentGeneratorPropertiesFilterMode.Hidden,
          },
        ];
      }, [] as DashboardComponentGeneratorPropertiesFilter[]);

      return privateAxios.put(`/dashboards/${dashboardId}`, {
        dashboard: {
          components_attributes: [
            {
              id: dashboardComponentId,
              generator_properties: {
                kind: form?.kind?.value,
                filters: parsedFilters,
              },
            },
          ],
        },
      });
    },
    ...options,
  });
}
